<!-- Class Selector Card -->
<div class="card card-flush mb-5">
  <div class="card-body py-5">
    <div class="row align-items-center">
      <div class="col-lg-4 col-md-6 col-12">
        <app-selectorini
          [label]="'results.selectClass' | translate"
          [options]="eventClasses"
          [displayKey]="'class_name'"
          [value]="selectedClass"
          (valueChange)="selectClass($event)"
          [floating]="true"
        />
      </div>
      @if (selectedClass && scoringConfig) {
        <div class="col-lg-8 col-md-6 col-12 mt-3 mt-lg-0">
          <div class="d-flex flex-wrap gap-2 justify-content-lg-end">
            <span class="badge badge-light-primary fs-7 fw-semibold px-3 py-2">
              <i class="fa fa-stopwatch me-1"></i>
              {{ scoringConfig.score_type === 'timed' ? ('scoring.timed' | translate) :
                 scoringConfig.score_type === 'points_high' ? ('scoring.pointsHigh' | translate) :
                 ('scoring.pointsLow' | translate) }}
            </span>
            <span class="badge badge-light-info fs-7 fw-semibold px-3 py-2">
              <i class="fa fa-users me-1"></i>
              {{ selectedClass.event_class_registrations?.length || 0 }} {{ 'results.participants' | translate }}
            </span>
            <span class="badge badge-light fs-7 fw-semibold px-3 py-2">
              <i class="fa fa-layer-group me-1"></i>
              {{ rounds.length }} {{ 'results.rounds' | translate }}
            </span>
          </div>
        </div>
      }
    </div>
  </div>
</div>

@if (loading) {
  <div class="loading-container">
    <div class="loading-spinner"></div>
    <p>{{ 'results.loading' | translate }}</p>
  </div>
} @else if (!selectedClass) {
  <div class="card card-flush">
    <div class="card-body">
      <div class="d-flex flex-column align-items-center justify-content-center py-15">
        <i class="fa fa-trophy fs-3x text-muted mb-4 opacity-50"></i>
        <h4 class="fw-bold text-gray-800 mb-1">{{ 'results.selectClassToView' | translate }}</h4>
        <p class="text-muted mb-0">{{ 'results.selectClassHint' | translate }}</p>
      </div>
    </div>
  </div>
} @else {
  <!-- Round Navigation Tabs -->
  @if (rounds.length > 0) {
    <div class="round-tabs-wrapper mb-5">
      <div class="round-tabs">
        @for (round of rounds; track round.round_id) {
          <button
            type="button"
            class="round-tab"
            [class.active]="selectedRound?.round_id === round.round_id && viewMode === 'round'"
            (click)="selectRound(round)"
          >
            <span class="round-number">{{ round.round_number }}</span>
            <span class="round-label">{{ round.round_name || ('rounds.round' | translate) + ' ' + round.round_number }}</span>
            @if (isRoundComplete(round)) {
              <i class="fa fa-check-circle text-success"></i>
            } @else if (getRoundResultsCount(round) > 0) {
              <span class="round-progress">{{ getRoundResultsCount(round) }}/{{ selectedClass.event_class_registrations?.length || 0 }}</span>
            }
          </button>
        }
        <button
          type="button"
          class="round-tab final-tab"
          [class.active]="viewMode === 'final'"
          (click)="showFinalStandings()"
        >
          <i class="fa fa-trophy"></i>
          <span class="round-label">{{ 'results.finalStandings' | translate }}</span>
        </button>
      </div>
    </div>
  }

  @if (rounds.length === 0) {
    <div class="alert alert-warning d-flex align-items-center p-5 mb-5">
      <i class="fa fa-exclamation-triangle fs-2x text-warning me-4"></i>
      <div>
        <h5 class="fw-bold mb-1">{{ 'results.noRoundsConfigured' | translate }}</h5>
        <p class="mb-0 text-muted">{{ 'results.configureRoundsHint' | translate }}</p>
      </div>
    </div>
  } @else if (viewMode === 'round' && selectedRound) {
    <!-- Round Results View -->
    <div class="card card-flush">
      <div class="card-header align-items-center">
        <div class="card-title">
          <h3 class="fw-bold mb-0">{{ selectedRound.round_name || ('rounds.round' | translate) + ' ' + selectedRound.round_number }}</h3>
          @if (isRoundComplete(selectedRound)) {
            <span class="badge badge-light-success ms-3">
              <i class="fa fa-check-circle me-1"></i>{{ 'results.complete' | translate }}
            </span>
          } @else if (isRoundStarted(selectedRound)) {
            <span class="badge badge-light-warning ms-3">
              <i class="fa fa-clock me-1"></i>{{ 'results.inProgress' | translate }}
            </span>
          } @else {
            <span class="badge badge-light-secondary ms-3">
              <i class="fa fa-minus-circle me-1"></i>{{ 'results.notStarted' | translate }}
            </span>
          }
        </div>
        <div class="card-toolbar">
          <button class="btn btn-sm btn-icon btn-light btn-active-light-primary" title="{{ 'results.exportResults' | translate }}">
            <i class="fa fa-download"></i>
          </button>
        </div>
      </div>
      <div class="card-body pt-0">
        @if (selectedClass.event_class_registrations?.length === 0) {
          <div class="text-center py-10 text-muted">
            <i class="fa fa-inbox fs-3x mb-4 d-block opacity-50"></i>
            <p class="mb-0">{{ 'results.noRegistrations' | translate }}</p>
          </div>
        } @else {
          <div class="table-responsive">
            <table class="table table-row-bordered table-row-dashed gy-4 align-middle fw-bold">
              <thead class="fs-7 text-gray-500 text-uppercase">
                <tr>
                  <th class="min-w-50px">{{ 'results.position' | translate }}</th>
                  <th class="min-w-200px">{{ 'results.participant' | translate }}</th>
                  <th class="min-w-120px">{{ isTimeBased ? ('results.time' | translate) : ('results.points' | translate) }}</th>
                  <th class="min-w-100px">{{ 'results.penalties' | translate }}</th>
                  <th class="min-w-100px">{{ 'results.status' | translate }}</th>
                  <th class="text-end min-w-100px">{{ 'common.actions' | translate }}</th>
                </tr>
              </thead>
              <tbody class="fs-6">
                @for (reg of selectedClass.event_class_registrations; track reg.class_registration_id) {
                  @let result = getResultForRegistration(reg);
                  <tr>
                    <td>
                      @if (result && result.status === 'recorded') {
                        <span class="badge badge-primary fs-7 fw-bold">{{ getPositionInRound(reg) }}</span>
                      } @else {
                        <span class="text-muted">-</span>
                      }
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="symbol symbol-40px symbol-circle me-3">
                          <span class="symbol-label bg-light-primary text-primary fw-bold">
                            {{ getInitials(reg.user?.first_name, reg.user?.last_name) }}
                          </span>
                        </div>
                        <div class="d-flex flex-column">
                          <span class="text-gray-800 fw-bold">{{ reg.user?.first_name }} {{ reg.user?.last_name }}</span>
                          <span class="text-muted fs-7">
                            <i class="fa fa-horse fs-8 me-1"></i>{{ reg.horse?.name || '-' }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      @if (result && result.status === 'recorded') {
                        <span class="fw-bold text-primary">
                          @if (isTimeBased) {
                            {{ formatTime(result.final_time_seconds) }}
                          } @else {
                            {{ formatPoints(result.final_points) }}
                          }
                        </span>
                      } @else {
                        <span class="text-muted">-</span>
                      }
                    </td>
                    <td>
                      @if (result && result.penalty_total !== 0) {
                        <span class="text-danger fw-semibold">
                          {{ result.penalty_total > 0 ? '+' : '' }}{{ isTimeBased ? formatTime(result.penalty_total) : formatPoints(result.penalty_total) }}
                        </span>
                      } @else {
                        <span class="text-muted">-</span>
                      }
                    </td>
                    <td>
                      @if (result) {
                        @switch (result.status) {
                          @case ('recorded') {
                            <span class="badge badge-light-success">{{ getStatusLabel(result.status) }}</span>
                          }
                          @case ('dnf') {
                            <span class="badge badge-light-warning">{{ getStatusLabel(result.status) }}</span>
                          }
                          @case ('dns') {
                            <span class="badge badge-light">{{ getStatusLabel(result.status) }}</span>
                          }
                          @case ('dq') {
                            <span class="badge badge-light-danger">{{ getStatusLabel(result.status) }}</span>
                          }
                          @default {
                            <span class="badge badge-light">{{ getStatusLabel(result.status) }}</span>
                          }
                        }
                      } @else {
                        <span class="text-muted fs-7">{{ 'results.noResultYet' | translate }}</span>
                      }
                    </td>
                    <td class="text-end">
                      @if (result) {
                        <button
                          type="button"
                          class="btn btn-sm btn-light btn-active-light-primary"
                          (click)="openResultEntryModal(reg, result)"
                        >
                          <i class="fa fa-pen me-1"></i>{{ 'common.edit' | translate }}
                        </button>
                      } @else {
                        <button
                          type="button"
                          class="btn btn-sm btn-light btn-active-light-primary"
                          (click)="openResultEntryModal(reg, result)"
                        >
                          <i class="fa fa-plus me-1"></i>{{ 'results.addResult' | translate }}
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  } @else if (viewMode === 'final') {
    <!-- Final Standings Config Bar -->
    <div class="card card-flush mb-5">
      <div class="card-body py-4">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <label class="fs-7 fw-semibold text-gray-500 text-uppercase mb-2 d-block">{{ 'results.rankingMethod' | translate }}</label>
            <div class="btn-group" role="group">
              @for (option of finalResultTypeOptions; track option.value) {
                <button
                  type="button"
                  class="btn btn-sm btn-light"
                  [class.active]="finalResultType === option.value"
                  (click)="updateFinalResultType(option.value)"
                  [disabled]="saving"
                >
                  @switch (option.value) {
                    @case ('combined_total') {
                      <i class="fa fa-calculator me-1"></i>
                    }
                    @case ('best_round') {
                      <i class="fa fa-star me-1"></i>
                    }
                    @case ('manual') {
                      <i class="fa fa-hand-pointer me-1"></i>
                    }
                  }
                  {{ option.label }}
                </button>
              }
            </div>
          </div>
          @if (isManualMode) {
            <button
              type="button"
              class="btn btn-sm save-btn"
              [disabled]="saving"
              (click)="saveFinalPositions()"
            >
              <i class="fa me-1" [class.fa-spinner]="saving" [class.fa-spin]="saving" [class.fa-save]="!saving"></i>
              {{ 'results.savePositions' | translate }}
            </button>
          }
        </div>
      </div>
    </div>

    <!-- Podium for Top 3 -->
    @if (aggregatedResults.length >= 3 && !isManualMode) {
      <div class="card card-flush mb-5">
        <div class="card-body py-8">
          <div class="d-flex justify-content-center align-items-end gap-4 podium-container">
            <!-- Second Place -->
            <div class="podium-place second text-center">
              <div class="symbol symbol-50px symbol-circle mb-3">
                <span class="symbol-label bg-secondary text-white fw-bold fs-5">
                  {{ getInitialsFromFullName(aggregatedResults[1]?.user_name) }}
                </span>
              </div>
              <div class="mb-3">
                <div class="fw-bold text-gray-800 fs-7">{{ aggregatedResults[1]?.user_name }}</div>
                <div class="text-muted fs-8">{{ aggregatedResults[1]?.horse_name || '-' }}</div>
                <div class="fw-bold text-primary fs-6 mt-1">
                  @if (isTimeBased) {
                    {{ isBestRoundMode ? formatTime(aggregatedResults[1]?.best_time) : formatTime(aggregatedResults[1]?.total_time) }}
                  } @else {
                    {{ isBestRoundMode ? formatPoints(aggregatedResults[1]?.best_points) : formatPoints(aggregatedResults[1]?.total_points) }}
                  }
                </div>
              </div>
              <div class="podium-block second">2</div>
            </div>

            <!-- First Place -->
            <div class="podium-place first text-center">
              <i class="fa fa-crown text-warning fs-2 mb-2"></i>
              <div class="symbol symbol-60px symbol-circle mb-3">
                <span class="symbol-label bg-warning text-white fw-bold fs-4">
                  {{ getInitialsFromFullName(aggregatedResults[0]?.user_name) }}
                </span>
              </div>
              <div class="mb-3">
                <div class="fw-bold text-gray-800 fs-6">{{ aggregatedResults[0]?.user_name }}</div>
                <div class="text-muted fs-7">{{ aggregatedResults[0]?.horse_name || '-' }}</div>
                <div class="fw-bold text-primary fs-5 mt-1">
                  @if (isTimeBased) {
                    {{ isBestRoundMode ? formatTime(aggregatedResults[0]?.best_time) : formatTime(aggregatedResults[0]?.total_time) }}
                  } @else {
                    {{ isBestRoundMode ? formatPoints(aggregatedResults[0]?.best_points) : formatPoints(aggregatedResults[0]?.total_points) }}
                  }
                </div>
              </div>
              <div class="podium-block first">1</div>
            </div>

            <!-- Third Place -->
            <div class="podium-place third text-center">
              <div class="symbol symbol-45px symbol-circle mb-3">
                <span class="symbol-label bg-bronze text-white fw-bold fs-6">
                  {{ getInitialsFromFullName(aggregatedResults[2]?.user_name) }}
                </span>
              </div>
              <div class="mb-3">
                <div class="fw-bold text-gray-800 fs-7">{{ aggregatedResults[2]?.user_name }}</div>
                <div class="text-muted fs-8">{{ aggregatedResults[2]?.horse_name || '-' }}</div>
                <div class="fw-bold text-primary fs-6 mt-1">
                  @if (isTimeBased) {
                    {{ isBestRoundMode ? formatTime(aggregatedResults[2]?.best_time) : formatTime(aggregatedResults[2]?.total_time) }}
                  } @else {
                    {{ isBestRoundMode ? formatPoints(aggregatedResults[2]?.best_points) : formatPoints(aggregatedResults[2]?.total_points) }}
                  }
                </div>
              </div>
              <div class="podium-block third">3</div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Full Results List -->
    <div class="card card-flush">
      <div class="card-header align-items-center">
        <div class="card-title">
          <h3 class="fw-bold mb-0">
            <i class="fa fa-list-ol me-2"></i>{{ 'results.allResults' | translate }}
          </h3>
        </div>
        @if (isManualMode) {
          <div class="card-toolbar">
            <span class="text-muted fs-7">
              <i class="fa fa-grip-vertical me-1"></i>{{ 'results.dragToReorder' | translate }}
            </span>
          </div>
        }
      </div>
      <div class="card-body pt-0">
        @if (aggregatedResults.length === 0) {
          <div class="text-center py-10 text-muted">
            <i class="fa fa-inbox fs-3x mb-4 d-block opacity-50"></i>
            <p class="mb-0">{{ 'results.noResults' | translate }}</p>
          </div>
        } @else {
          <div class="table-responsive">
            <table class="table table-row-bordered table-row-dashed gy-4 align-middle fw-bold" cdkDropList (cdkDropListDropped)="onDrop($event)" [cdkDropListDisabled]="!isManualMode">
              <thead class="fs-7 text-gray-500 text-uppercase">
                <tr>
                  @if (isManualMode) {
                    <th class="min-w-30px"></th>
                  }
                  <th class="min-w-50px">{{ 'results.position' | translate }}</th>
                  <th class="min-w-200px">{{ 'results.participant' | translate }}</th>
                  @for (round of rounds; track round.round_id) {
                    <th class="min-w-80px text-center">R{{ round.round_number }}</th>
                  }
                  <th class="min-w-100px text-end">{{ isBestRoundMode ? ('results.best' | translate) : ('results.total' | translate) }}</th>
                </tr>
              </thead>
              <tbody class="fs-6">
                @for (agg of aggregatedResults; track agg.class_registration_id; let i = $index) {
                  <tr cdkDrag [cdkDragDisabled]="!isManualMode">
                    @if (isManualMode) {
                      <td cdkDragHandle class="cursor-grab">
                        <i class="fa fa-grip-vertical text-gray-400"></i>
                      </td>
                    }
                    <td>
                      @switch (agg.final_position) {
                        @case (1) {
                          <span class="badge badge-warning fs-7"><i class="fa fa-trophy text-white"></i></span>
                        }
                        @case (2) {
                          <span class="badge badge-secondary fs-7"><i class="fa fa-medal text-white"></i></span>
                        }
                        @case (3) {
                          <span class="badge badge-bronze fs-7"><i class="fa fa-medal text-white"></i></span>
                        }
                        @default {
                          <span class="badge badge-light fs-7 fw-bold text-gray-600">{{ agg.final_position }}</span>
                        }
                      }
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="symbol symbol-35px symbol-circle me-3">
                          <span class="symbol-label bg-light-primary text-primary fw-bold fs-7">
                            {{ getInitialsFromFullName(agg.user_name) }}
                          </span>
                        </div>
                        <div class="d-flex flex-column">
                          <span class="text-gray-800 fw-bold">{{ agg.user_name }}</span>
                          <span class="text-muted fs-7">
                            <i class="fa fa-horse fs-8 me-1"></i>{{ agg.horse_name || '-' }}
                          </span>
                        </div>
                      </div>
                    </td>
                    @for (round of rounds; track round.round_id) {
                      @let roundResult = agg.rounds[round.round_id];
                      <td class="text-center">
                        @if (roundResult && roundResult.status === 'recorded') {
                          <span class="fw-semibold" [class.text-primary]="isBestRound(agg, round)">
                            @if (isTimeBased) {
                              {{ formatTime(roundResult.final_time_seconds) }}
                            } @else {
                              {{ formatPoints(roundResult.final_points) }}
                            }
                          </span>
                        } @else if (roundResult) {
                          <span class="text-muted fs-7">{{ getStatusLabel(roundResult.status) }}</span>
                        } @else {
                          <span class="text-muted">-</span>
                        }
                      </td>
                    }
                    <td class="text-end">
                      <span class="btn btn-sm btn-light fw-bold">
                        @if (isTimeBased) {
                          {{ isBestRoundMode ? formatTime(agg.best_time) : formatTime(agg.total_time) }}
                        } @else {
                          {{ isBestRoundMode ? formatPoints(agg.best_points) : formatPoints(agg.total_points) }}
                        }
                      </span>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  }
}
